import React, { useState } from 'react';
import { Button, Typography, Grid, Paper, Card, CardContent, IconButton, InputAdornment, Divider } from '@mui/material';
import { Visibility, VisibilityOff, Lock, Person, Home, LockClock, LockSharp, HomeTwoTone, FamilyRestroom, Pin } from '@mui/icons-material';
import { FormProvider, useForm } from 'react-hook-form';
import { DateInput, Dropdown, TextInput, Autocomplete } from 'qubCommon/Inputs';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getClubsPublic } from 'apis/controllers/club';
import { debounce } from 'lodash';
import { PasswordStrengthBar } from 'ui-component/change-password/passwordStrengthBar';
import { getLookups } from 'apis/controllers/backoffice';
import ExistingAccountNotice from '../ExistingAccountNotice';
import { useYupValidationResolver } from 'hooks/yupValidation/useYupValidationResolver';
import * as yup from 'yup';
import format from 'date-fns/format';
import { getClubs, register, transferAccount } from 'apis/controllers/membership';
import AccountCreatedSuccess from './AccountCreatedSuccess';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import MinorNotice from '../MinorNotice';

const RegisterForm = ({ formData, transfer = false }) => {
    const { t } = useTranslation();
    const [autocompleteValue, setAutoCompleteValue] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [accountCreated, setAccountCreated] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const minDOB = new Date();
    minDOB.setFullYear(minDOB.getFullYear() - 18);
    const yupResolver = useYupValidationResolver(
        yup.object().shape({
            // password: yup.string().max(255).required(t('Register.PasswordRequired')),
            // confirmPassword: yup
            //     .string()
            //     .max(255)
            //     .oneOf([yup.ref('password')], t('Register.PasswordsDontMatch'))
            //     .required(t('Register.ConfirmPasswordRequired')),
            dob: yup
                .string()
                .required(t('Register.DOBRequired'))
                .test('is-old-enough', 'You must be at least 18 years old', (value) => {
                    const parsedDate = new Date(value);
                    return parsedDate <= minDOB;
                }),
            genderId: yup.number().required(t('Register.GenderRequired')),
            organizationId: yup
                .object()
                .shape({
                    id: yup.string().required('Organization is a required field') // Ensures `id` is required
                })
                .required('Organization is a required field'),
            code: yup.string().when([], {
                is: () => transfer,
                then: (schema) => schema.required('Invitation Pin is Required'),
                otherwise: (schema) => schema.notRequired()
            })
        })
    );

    const organizationsQuery = useQuery('organizations', async () => {
        var res = await getLookups({ dataKey: 'OrgProvinces' });
        return res;
    });

    const gendersQuery = useQuery('genders', async () => {
        const res = await getLookups({ dataKey: 'Genders' });
        return res;
    });

    const countriesQuery = useQuery('countries', async () => {
        const res = await getLookups({ dataKey: 'Countries' });
        return res;
    });

    const form = useForm({
        defaultValues: {
            ...formData?.person,
            parentOrganizationId: formData?.person?.parentOrganizationId?.toUpperCase(),
            organizationId: formData?.person?.organizationId
                ? {
                      id: formData?.person?.organizationId,
                      title: formData?.person?.organizationTitle
                  }
                : null,
            dob: formData?.person?.dob ?? null,
            ottId: formData?.ottId
        },
        resolver: yupResolver
    });

    const clubsQuery = useQuery(['clubs', autocompleteValue, form.watch('parentOrganizationId')], async () => {
        var res = await getClubs({
            search: autocompleteValue,
            parentGuid: form.watch('parentOrganizationId') ?? formData?.person?.organizationId
        });
        return res;
    });

    const handleAutoCompleteChange = debounce(function (value) {
        console.log(value);
        setAutoCompleteValue(value);
    }, 500);

    const onSubmit = async (data) => {
        await setSubmitting(true);
        let code;
        try {
            code = parseInt(data?.code);
        } catch (e) {
            console.log(e);
            code = '';
        }
        try {
            const res = transfer
                ? await transferAccount({ ...data, organizationId: data?.organizationId?.id ?? null, code })
                : await register({ ...data, organizationId: data?.organizationId?.id ?? null });
            if (res?.status === 'success') setAccountCreated(true);
            enqueueSnackbar(t(res?.message), { variant: res?.status });
        } catch (e) {
            console.log(e);
        } finally {
            setSubmitting(false);
        }
    };

    const existingPerson = formData?.found;
    const minorOrEmptyDob = formData?.minorOrEmptyDob;
    const dependents = formData?.person?.dependents;
    const userExists = formData?.userExists;

    if (accountCreated) return <AccountCreatedSuccess />;
    return (
        <Paper elevation={3} style={{ maxWidth: '700px', padding: '30px', margin: 'auto' }}>
            <Typography variant="h3" align="center" gutterBottom>
                {t('BecomeMember')}!
            </Typography>
            <Typography variant="subtitle1" align="center" color="textSecondary">
                {t('Register.Subtitle')}
            </Typography>

            <FormProvider {...form}>
                <Grid container spacing={3}>
                    {/* Credentials Section */}
                    <Grid item xs={12}>
                        <Typography variant="h4" gutterBottom>
                            <LockSharp color="primary" /> {t('Credentials')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextInput
                            name="email"
                            label={t('Username')}
                            fullWidth
                            startAdornment={
                                <InputAdornment position="start">
                                    <Person />
                                </InputAdornment>
                            }
                            defaultFromProps={false}
                        />
                    </Grid>
                    {transfer && (
                        <Grid item xs={12}>
                            <TextInput
                                name="code"
                                label={t('InvitationPin')}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <Pin />
                                    </InputAdornment>
                                }
                            />
                        </Grid>
                    )}

                    {!userExists && (
                        <>
                            <Grid item xs={12}>
                                <TextInput
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    label={t('Password')}
                                    fullWidth
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <Lock />
                                        </InputAdornment>
                                    }
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    defaultFromProps={false}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextInput
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    label={t('ConfirmYourPassword')}
                                    fullWidth
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <Lock />
                                        </InputAdornment>
                                    }
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    defaultFromProps={false}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <PasswordStrengthBar password={form?.watch('password') ?? ''} />
                            </Grid>
                        </>
                    )}
                    {existingPerson && (
                        <Grid item xs={12}>
                            <ExistingAccountNotice />
                        </Grid>
                    )}
                    {minorOrEmptyDob && (
                        <Grid item xs={12}>
                            <MinorNotice />
                        </Grid>
                    )}

                    {/* Personal Info Section */}
                    <Grid item xs={12}>
                        <Typography variant="h4" gutterBottom>
                            <Person color="primary" /> {t('Personal Info')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextInput label={t('LastName')} fullWidth name="lastName" defaultFromProps={false} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextInput label={t('FirstName')} fullWidth name="firstName" defaultFromProps={false} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextInput label={t('FathersName')} fullWidth name="fathersName" defaultFromProps={false} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextInput label={t('MothersName')} fullWidth name="mothersName" defaultFromProps={false} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DateInput label={t('DOB')} fullWidth name="dob" defaultFromProps={false} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Dropdown label={t('Gender')} fullWidth name="genderId" options={gendersQuery?.data} />
                    </Grid>

                    {/* Organization Selection */}
                    <Grid item xs={12}>
                        <Dropdown
                            label={t('MembershipProvince')}
                            fullWidth
                            name="parentOrganizationId"
                            options={organizationsQuery?.data}
                            changeFn={() => {
                                form?.setValue('organizationId', null);
                                setAutoCompleteValue(null);
                            }}
                        />
                    </Grid>

                    {/* Organization Selection */}
                    <Grid item xs={12}>
                        <Autocomplete
                            label={t('Club/School')}
                            name="organizationId"
                            disabled={!form?.watch('parentOrganizationId')}
                            labelName="title"
                            options={clubsQuery?.data || []}
                            onInputChange={(_, value) => handleAutoCompleteChange(value)}
                        />
                        {form.formState.errors.organization?.id && <p>{form.formState.errors.organization.id.message}</p>}
                    </Grid>

                    {/* Address Section */}
                    <Grid item xs={12}>
                        <Typography variant="h4" gutterBottom>
                            <HomeTwoTone color="primary" /> {t('Address Info')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextInput label={t('Number')} fullWidth name="streetNr" defaultFromProps={false} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextInput label={t('Street')} fullWidth name="address1" defaultFromProps={false} />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <TextInput label={t('Address2')} fullWidth name="address2" defaultFromProps={false} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextInput label={t('ZipCode')} fullWidth name="zipCode" defaultFromProps={false} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextInput label={t('Region')} fullWidth name="region" defaultFromProps={false} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextInput label={t('City')} fullWidth name="city" defaultFromProps={false} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Dropdown label={t('Country')} fullWidth name="countryId" options={countriesQuery?.data} />
                    </Grid>
                    {/* Dependent Members Section */}
                    {dependents?.length > 0 && (
                        <Grid item xs={12}>
                            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
                                <FamilyRestroom sx={{ fontSize: 30, color: 'primary.main', mr: 1 }} />
                                {t('Register.Dependents')}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            {dependents.map((dependent, index) => {
                                const birthYear = parseInt(dependent.dob.split('/')[2], 10);
                                const isAdult = birthYear <= new Date().getFullYear() - 18;
                                return (
                                    <Typography key={index} variant="body1" sx={{ mb: 1 }}>
                                        <Person /> {dependent.name} ({t('DOB')}: {dependent.dob}){' '}
                                    </Typography>
                                );
                            })}
                        </Grid>
                    )}
                    {/* Submit Button */}
                    <Grid item xs={12}>
                        <LoadingButton
                            loading={submitting}
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                            onClick={form.handleSubmit(onSubmit)}
                        >
                            {existingPerson ? t('ClaimAccount') : t('Submit')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </FormProvider>
        </Paper>
    );
};

export default RegisterForm;
